import React, { forwardRef, SyntheticEvent } from "react";
import styled from "@emotion/styled";
import * as tokens from "../../design-tokens";

const TOGGLE_SIZES = {
  large: 20,
  medium: 16,
};

const SWITCH_SIZES = {
  large: {
    width: 44,
    height: 24,
  },
  medium: {
    width: 34,
    height: 20,
  },
};

type SwitchSize = "medium" | "large";

interface SwitchProps {
  size?: SwitchSize;
  name?: string;
  className?: string;
  checked: boolean;
  testId?: string;
  onChange: (checked: boolean) => void;
  onClick: (e: SyntheticEvent) => void;
}

const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className = "",
      name,
      checked,
      size = "medium",
      testId,
      onChange,
      onClick,
    },
    ref
  ) => {
    return (
      <SwitchContainer className={className} switchSize={size}>
        <Input
          ref={ref}
          type="checkbox"
          name={name}
          switchSize={size}
          checked={checked}
          onClick={onClick}
          data-testid={testId}
          onChange={(e) => onChange?.(e.target.checked)}
        />
        <Toggle className="toggle" toggleSize={size} />
      </SwitchContainer>
    );
  }
);

const SwitchContainer = styled.label<{ switchSize: SwitchSize }>`
  position: relative;
  display: inline-block;
  width: ${(props) => SWITCH_SIZES[props.switchSize].width}px;
  height: ${(props) => SWITCH_SIZES[props.switchSize].height}px;
`;

const Toggle = styled.span<{ toggleSize: SwitchSize }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${tokens.HEROBLUE_70};
  border-radius: 100px;

  :before {
    content: "";
    border-radius: 50%;
    position: absolute;
    height: ${(props) => TOGGLE_SIZES[props.toggleSize]}px;
    width: ${(props) => TOGGLE_SIZES[props.toggleSize]}px;
    left: 2px;
    bottom: 2px;
    background-color: ${tokens.WHITE};
    transition: transform 0.3s;
  }
`;

const Input = styled.input<{ switchSize: SwitchSize }>`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + .toggle {
    background-color: ${tokens.HEROBLUE_50};
  }

  &:focus + .toggle {
    box-shadow: 0 0 1px ${tokens.HEROBLUE_50};
  }

  &:checked + .toggle:before {
    transform: ${(props) =>
      `translateX(${
        SWITCH_SIZES[props.switchSize].width -
        SWITCH_SIZES[props.switchSize].height
      }px)`};
  }

  &:disabled + .toggle {
    cursor: not-allowed;
  }

  &:disabled + .toggle:before {
    cursor: not-allowed;
    background-color: ${tokens.HEROBLUE_80};
  }
`;

export { Switch };
export type { SwitchProps };
